import { useState, useEffect, useCallback } from "react"
import { useAuth0 } from "@auth0/auth0-react"

// API URL from environment
const apiUrl = process.env.REACT_APP_API_URL 
/**
 * Hook for fetching tasks
 */
export const useFetchTasks = (groupId, refreshTrigger = 0) => {
  const { getAccessTokenSilently } = useAuth0()
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTasks = useCallback(async () => {
    console.log("fetchTasks called with groupId:", groupId)

    if (!groupId) {
      console.log("No groupId provided, returning empty tasks array")
      setTasks([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      // Convert groupId to integer if it's a string
      const numericGroupId = typeof groupId === "string" ? Number.parseInt(groupId, 10) : groupId
      
      const response = await fetch(`${apiUrl}/api/tasks?groupId=${numericGroupId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()
      
      // Check if data is an array
      if (!Array.isArray(data)) {
        console.error("API returned non-array data:", data)
        setTasks([])
      } else {
        setTasks(data)
      }
    } catch (error) {
      console.error("Error fetching tasks:", error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }, [groupId, getAccessTokenSilently])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks, refreshTrigger])

  return { tasks, loading, error, refetch: fetchTasks }
}

/**
 * Hook for fetching filtered and paginated tasks
 */
export const useFetchFilteredTasks = (filter = {}, page = 1, pageSize = 10, refreshTrigger = 0) => {
  const { getAccessTokenSilently } = useAuth0()
  const [tasks, setTasks] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchFilteredTasks = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      // Build query string from filter
      const queryParams = new URLSearchParams()
      queryParams.append("page", page)
      queryParams.append("pageSize", pageSize)

      // Add filter parameters
      if (filter.groupId) {
        queryParams.append(
          "groupId",
          typeof filter.groupId === "string" ? Number.parseInt(filter.groupId, 10) : filter.groupId,
        )
      }
      if (filter.positionId) {
        queryParams.append(
          "positionId",
          typeof filter.positionId === "string" ? Number.parseInt(filter.positionId, 10) : filter.positionId,
        )
      }
      if (filter.status) {
        queryParams.append("status", filter.status)
      }
      if (filter.type) {
        queryParams.append("type", filter.type)
      }
      if (filter.searchTerm) {
        queryParams.append("searchTerm", filter.searchTerm)
      }
      if (filter.startDate) {
        queryParams.append("startDate", filter.startDate)
      }
      if (filter.endDate) {
        queryParams.append("endDate", filter.endDate)
      }

      const response = await fetch(`${apiUrl}/api/tasks/filtered?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()
      setTasks(data.items)
      setTotalCount(data.totalCount)
      setTotalPages(data.totalPages)
    } catch (error) {
      console.error("Error fetching filtered tasks:", error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }, [filter, page, pageSize, getAccessTokenSilently])

  useEffect(() => {
    fetchFilteredTasks()
  }, [fetchFilteredTasks, refreshTrigger])

  return {
    tasks,
    totalCount,
    totalPages,
    currentPage: page,
    loading,
    error,
    refetch: fetchFilteredTasks,
  }
}

/**
 * Hook for fetching a single task
 */
export const useFetchTask = (taskId, refreshTrigger = 0) => {
  const { getAccessTokenSilently } = useAuth0()
  const [task, setTask] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTask = useCallback(async () => {
    if (!taskId) {
      setTask(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${apiUrl}/api/tasks/${taskId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()
      setTask(data)
    } catch (error) {
      console.error("Error fetching task:", error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }, [taskId, getAccessTokenSilently])

  useEffect(() => {
    fetchTask()
  }, [fetchTask, refreshTrigger])

  return { task, loading, error, refetch: fetchTask }
}

/**
 * Hook for task mutations (create, update, add work log)
 */
export const useTaskMutations = () => {
  const { getAccessTokenSilently } = useAuth0()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  /**
   * Create a new task
   */
  const createTask = async (taskData) => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(false)

      // Ensure IDs are numbers where needed
      const processedData = {
        ...taskData,
        groupId: typeof taskData.groupId === "string" ? Number.parseInt(taskData.groupId, 10) : taskData.groupId,
        positionId:
          typeof taskData.positionId === "string" ? Number.parseInt(taskData.positionId, 10) : taskData.positionId,
        // Only convert these if they exist
        reportsToPositionId: taskData.reportsToPositionId
          ? typeof taskData.reportsToPositionId === "string"
            ? Number.parseInt(taskData.reportsToPositionId, 10)
            : taskData.reportsToPositionId
          : null,
        parentTaskId: taskData.parentTaskId
          ? typeof taskData.parentTaskId === "string"
            ? Number.parseInt(taskData.parentTaskId, 10)
            : taskData.parentTaskId
          : null,
        effort: taskData.effort
          ? typeof taskData.effort === "string"
            ? Number.parseInt(taskData.effort, 10)
            : taskData.effort
          : null,
      }

      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${apiUrl}/api/tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(processedData),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => null)
        throw new Error(errorData?.detail || errorData?.message || errorData?.title || `API error: ${response.status}`)
      }

      const data = await response.json()
      setSuccess(true)
      return data
    } catch (error) {
      console.error("Error creating task:", error)
      setError(error.message)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Update an existing task
   */
  const updateTask = async (taskId, taskData) => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(false)

      // Ensure IDs are numbers where needed
      const processedData = {
        ...taskData,
        id: typeof taskId === "string" ? Number.parseInt(taskId, 10) : taskId,
        groupId: typeof taskData.groupId === "string" ? Number.parseInt(taskData.groupId, 10) : taskData.groupId,
        positionId:
          typeof taskData.positionId === "string" ? Number.parseInt(taskData.positionId, 10) : taskData.positionId,
        // Only convert these if they exist
        reportsToPositionId: taskData.reportsToPositionId
          ? typeof taskData.reportsToPositionId === "string"
            ? Number.parseInt(taskData.reportsToPositionId, 10)
            : taskData.reportsToPositionId
          : null,
        parentTaskId: taskData.parentTaskId
          ? typeof taskData.parentTaskId === "string"
            ? Number.parseInt(taskData.parentTaskId, 10)
            : taskData.parentTaskId
          : null,
        effort: taskData.effort
          ? typeof taskData.effort === "string"
            ? Number.parseInt(taskData.effort, 10)
            : taskData.effort
          : null,
      }

      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${apiUrl}/api/tasks/${taskId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(processedData),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => null)
        throw new Error(errorData?.detail || errorData?.message || errorData?.title || `API error: ${response.status}`)
      }

      const data = await response.json()
      setSuccess(true)
      return data
    } catch (error) {
      console.error("Error updating task:", error)
      setError(error.message)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Add a work log to a task
   */
  const addWorkLog = async (taskId, workLogData) => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(false)

      // Ensure taskId is a number
      const numericTaskId = typeof taskId === "string" ? Number.parseInt(taskId, 10) : taskId

      // Process work log data
      const processedData = {
        ...workLogData,
        taskId: numericTaskId,
        hours: typeof workLogData.hours === "string" ? Number.parseFloat(workLogData.hours) : workLogData.hours,
      }

      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${apiUrl}/api/tasks/${numericTaskId}/work-logs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(processedData),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => null)
        throw new Error(errorData?.detail || errorData?.message || errorData?.title || `API error: ${response.status}`)
      }

      const data = await response.json()
      setSuccess(true)
      return data
    } catch (error) {
      console.error("Error adding work log:", error)
      setError(error.message)
      throw error
    } finally {
      setLoading(false)
    }
  }

  return {
    createTask,
    updateTask,
    addWorkLog,
    loading,
    error,
    success,
  }
}

// Export default for convenience
export default useTaskMutations

